import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'

// The (i) beside a stage, a field or a card: one reason, one press away.
//
// # WHY 200 CHARACTERS
//
// The spec keeps a helper line to 80 characters and a toggletip to 200, and
// sends anything longer to a drawer. Past that length a bubble covers the thing
// it explains. A development build warns when a caller passes more, so the
// limit is met when the copy is written, not found on a review.
//
// # HOW IT IS ANNOUNCED
//
// The words go into a polite live region that sits beside the button and is
// always in the document, which is what makes a screen reader read them on
// open. The bubble a sighted reader sees is drawn in a portal on the body,
// because a bubble inside a scrolling table or a panel with overflow hidden is
// cut off, and it is hidden from assistive technology so nothing is read twice.
//
// # THE HOVER MODE
//
// The product officer's review of 17 September asked for the explanations on
// the working screens to sit behind a hover rather than a press. With hover
// set, the bubble opens when a mouse rests on the (i) and when the keyboard
// focuses it, and closes when the mouse leaves, focus moves on, or Escape is
// pressed. A press still pins it open, which is the only way a touch screen,
// where nothing hovers, can read it at all. The mouse may cross from the (i)
// to the bubble without it closing, so a longer reason can be read with the
// pointer resting on it.

const LIMIT = 200
const WIDTH = 260
// Long enough to cross the gap between the (i) and the bubble, short enough
// that a bubble left behind does not linger over the next thing looked at.
const LEAVE_MS = 150

type Mode = 'closed' | 'hover' | 'pinned'

export function Toggletip({ label, children, hover = false }: { label: string; children: string; hover?: boolean }) {
  const [mode, setMode] = useState<Mode>('closed')
  const open = mode !== 'closed'
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const button = useRef<HTMLButtonElement>(null)
  const bubble = useRef<HTMLSpanElement>(null)
  const leaveTimer = useRef<number | null>(null)
  // Escape hands focus back to the button, and in hover mode a focus opens the
  // bubble. This is what stops the close from reopening it.
  const refocusing = useRef(false)

  useEffect(() => {
    if (import.meta.env.DEV && children.length > LIMIT) {
      console.warn('Toggletip "' + label + '" holds ' + children.length + ' characters; the limit is ' + LIMIT + '. Move the rest to a drawer.')
    }
  }, [children, label])

  const cancelLeave = useCallback(() => {
    if (leaveTimer.current !== null) {
      window.clearTimeout(leaveTimer.current)
      leaveTimer.current = null
    }
  }, [])

  useEffect(() => cancelLeave, [cancelLeave])

  const place = useCallback(() => {
    const b = button.current
    if (!b) return
    const r = b.getBoundingClientRect()
    const height = bubble.current?.offsetHeight ?? 0
    const left = Math.max(8, Math.min(r.left - 8, window.innerWidth - WIDTH - 8))
    let top = r.bottom + 8
    if (height > 0 && top + height > window.innerHeight - 8 && r.top - 8 - height > 8) top = r.top - 8 - height
    setPos({ top, left })
  }, [])

  // Placed before paint and again once the bubble has a height, so a bubble
  // near the bottom of the window opens above the button rather than off it.
  useLayoutEffect(() => {
    if (!open) return
    place()
    const raf = requestAnimationFrame(place)
    return () => cancelAnimationFrame(raf)
  }, [open, place])

  useEffect(() => {
    if (!open) return
    // Listened for in the capture phase and stopped there, so Escape closes
    // the bubble and nothing else: a toggletip inside a drawer must not close
    // the drawer with it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        cancelLeave()
        setMode('closed')
        refocusing.current = true
        button.current?.focus()
        refocusing.current = false
      }
    }
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node | null
      if (t && (button.current?.contains(t) || bubble.current?.contains(t))) return
      setMode('closed')
    }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('pointerdown', onPointer)
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('pointerdown', onPointer)
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place, cancelLeave])

  // A touch has no hover, and a tap fires pointerenter on its way to the click.
  // Opening on it would make the click that follows close the bubble again.
  const enter = (e: ReactPointerEvent) => {
    if (!hover || e.pointerType === 'touch') return
    cancelLeave()
    setMode((m) => (m === 'closed' ? 'hover' : m))
  }
  const leave = (e: ReactPointerEvent) => {
    if (!hover || e.pointerType === 'touch') return
    cancelLeave()
    leaveTimer.current = window.setTimeout(() => {
      leaveTimer.current = null
      setMode((m) => (m === 'hover' ? 'closed' : m))
    }, LEAVE_MS)
  }

  return (
    <span className="kit-tip">
      <button
        ref={button}
        type="button"
        className="kit-tip-button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => {
          cancelLeave()
          // Without hover a press opens and closes. With it, a press on a
          // bubble the hover or the focus opened pins it, and a second press
          // closes it.
          setMode((m) => (hover ? (m === 'pinned' ? 'closed' : 'pinned') : m === 'closed' ? 'pinned' : 'closed'))
        }}
        onPointerEnter={enter}
        onPointerLeave={leave}
        onFocus={() => {
          if (hover && !refocusing.current) setMode((m) => (m === 'closed' ? 'hover' : m))
        }}
        onBlur={() => {
          if (hover) setMode((m) => (m === 'hover' ? 'closed' : m))
        }}
      >
        i
      </button>
      <span className="vh" role="status">
        {open ? children : ''}
      </span>
      {open &&
        createPortal(
          <span
            ref={bubble}
            className="kit-tip-bubble"
            aria-hidden="true"
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: WIDTH }}
            onPointerEnter={enter}
            onPointerLeave={leave}
          >
            {children}
          </span>,
          document.body,
        )}
    </span>
  )
}
